<template>
  <div class="dashboard dashboard-ds">
    <div class="dashboard-page-header">
      <div class="dashboard-page-header__icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      </div>
      <div class="dashboard-page-header__text">
        <h1 class="dashboard-page-header__title">Dashboard</h1>
        <p class="dashboard-page-header__subtitle">
          Visão geral do seu sistema de telefonia.
        </p>
      </div>
    </div>
    <ErrorAlert :error="error" :centered="true" />

    <dashboard-filters
      :is-admin="isAdmin"
      :filter-users="filterUsers"
      :filter-users-options="filterUsersOptions"
      :filter-users-labels="userLabels"
      :filter-date="filterDate"
      :filter-date-options="filterDateOptions"
      :filter-type="filterType"
      :filter-type-options="filterTypeOptions"
      :filter-hangup-cause="filterHangupCause"
      :filter-status-options="filterStatusOptions"
      :has-pending="hasPendingChanges"
      :users-loading="userOptionsLoading"
      :users-error="userOptionsError"
      :users-has-more="userOptionsHasMore"
      :users-too-many="userOptionsTooMany"
      :users-select-all-limit="maxSelectableUsers"
      @load-more-users="loadMoreUsers"
      @select-all-users="selectAllUsers"
      @search-users="searchUsers"
      @change-users="onChangeUsers"
      @change-date="onChangeDate"
      @change-type="onChangeType"
      @change-status="onChangeStatus"
      @apply="applyFilters" />

    <dashboard-kpis
      :calls-total="callsTotal"
      :hit-rate="hitRate"
      :answers-total="answersTotal"
      :durations-total-time="durationsTotalTime"
      :extensions-total="extensionsTotal"
      :tma="tma" />

    <div class="row dashboard-row--gap">
      <div class="col-md-12">
        <user-performance-table
          :by-extension="patienceByExtension"
          @export-html="exportReportHtml" />
      </div>
    </div>

    <div class="row">
      <div class="col-md-8">
        <user-calls-chart
          :chart-data="chartData"
          :granularity="granularity"
          @set-granularity="setGranularity" />
      </div>
      <div class="col-md-4">
        <hangup-cause-donut
          :donut-data="donutData"
          :legend="statusLegend"
          @toggle-status="toggleStatusFilter" />
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import CDRDataSource from './../services/cdr-data-source'
import { HANGUPCAUSES_MAPPER } from './../utils/phone'
import DashboardFilters from './Dashboard/DashboardFilters.vue'
import DashboardKpis from './Dashboard/DashboardKpis.vue'
import UserPerformanceTable from './Dashboard/UserPerformanceTable.vue'
import HangupCauseDonut from './Dashboard/HangupCauseDonut.vue'
import UserCallsChart from './Dashboard/UserCallsChart.vue'
import { generateDashboardReportHtml } from './../utils/dashboardReportHtml'
import {
  statusColor,
  CALL_TYPE_OPTIONS,
  applyChartDefaults,
  buildPeriodPresets,
  secondsToTimeParser,
  formatPeriodLabel,
  formatReportTimestamp,
  formatIsoDay
} from './Dashboard/dashboard-helpers'

// Tamanho da página do filtro de Usuários.
const USER_OPTIONS_PAGE_SIZE = 100
// Teto de ramais numa seleção feita de uma vez. Os ramais selecionados vão na query string
// do dashboard-statistics (~13 bytes cada); acima disso a URL passa do buffer de header do
// proxy/servidor e a requisição é recusada. 400 ≈ 5 KB, com folga.
const MAX_SELECTABLE_USERS = 400

export default {
  name: 'Dashboard',
  components: {
    DashboardFilters,
    DashboardKpis,
    UserPerformanceTable,
    HangupCauseDonut,
    UserCallsChart
  },
  data () {
    const filterDateOptions = buildPeriodPresets()
    return {
      error: null,
      // Visibilidade por perfil
      isAdmin: false,
      ownExtension: null,
      // Filtros
      filterUsers: [],
      // Página(s) carregadas do servidor para o termo de busca atual.
      userOptions: [],
      // Rótulo (nome + ramal) de todo ramal já visto — mantém o nome dos selecionados
      // quando uma nova busca troca a lista (botão, topo da lista e relatório exportado).
      userLabels: {},
      userSearch: '',
      // Paginação (infinite scroll) das opções de Usuários
      userOptionsSkip: 0,
      userOptionsHasMore: true,
      userOptionsLoading: false,
      userOptionsError: null,
      // Identifica a requisição em voo: respostas de uma busca anterior são descartadas.
      userOptionsRequest: 0,
      // Promise da página em voo (o "Selecionar todos" espera por ela em vez de disparar outra).
      pendingUserOptions: null,
      // Resultado da busca grande demais para selecionar de uma vez (ver MAX_SELECTABLE_USERS).
      userOptionsTooMany: false,
      filterDate: filterDateOptions[0].value,
      filterDateOptions,
      filterType: CALL_TYPE_OPTIONS[0].value,
      filterTypeOptions: CALL_TYPE_OPTIONS,
      filterHangupCause: [],
      granularity: 'hora',
      // Filtros efetivamente aplicados (snapshot). Os campos filter* acima são o
      // RASCUNHO que o usuário edita; só viram "aplicados" ao clicar em Aplicar.
      // O buildFilter e o auto-refresh usam sempre os aplicados.
      applied: {
        users: [],
        date: filterDateOptions[0].value,
        type: CALL_TYPE_OPTIONS[0].value,
        hangupCause: []
      },
      // Resultados das agregações
      donutData: { labels: [], hangups: {} },
      chartData: { labels: [], extensions: {}, calls: {} },
      patienceByExtension: {},
      callsTotal: 0,
      answersTotal: 0,
      durationsTotal: 0,
      durationsTotalTime: '',
      hitRate: 0,
      tma: '',
      extensionsTotal: 0,
      // Auto-refresh
      timer: null,
      timeout: null,
      refreshTime: 30,
      refreshCount: 30
    }
  },
  computed: {
    maxSelectableUsers () {
      return MAX_SELECTABLE_USERS
    },
    // Lista exibida no filtro de Usuários: o que veio do servidor para a busca atual e, sem
    // busca, os selecionados que ainda não estão nela (no topo, para não sumirem da lista).
    filterUsersOptions () {
      if (this.userSearch) return this.userOptions
      const listed = new Set(this.userOptions.map(o => o.value))
      const pinned = this.filterUsers
        .filter(value => !listed.has(value) && this.userLabels[value])
        .map(value => ({ value, text: this.userLabels[value] }))
      return pinned.concat(this.userOptions)
    },
    // Opções do filtro de status: mesma lista canônica do Histórico de Chamadas
    // (HANGUPCAUSES_MAPPER deduplicado por rótulo). Estável e independente dos dados do período.
    filterStatusOptions () {
      const seen = new Set()
      const options = []
      Object.values(HANGUPCAUSES_MAPPER).forEach(label => {
        if (!seen.has(label)) {
          seen.add(label)
          options.push({ text: label, value: label })
        }
      })
      return options
    },
    // Legenda compartilhada (label + cor estável + estado do cross-filter), usada
    // pelo donut e pela tabela para garantir cores consistentes.
    statusLegend () {
      const labels = (this.donutData && this.donutData.labels) || []
      return labels.map(label => ({
        label,
        color: statusColor(label),
        active: this.filterHangupCause.length === 0 || this.filterHangupCause.indexOf(label) >= 0
      }))
    },
    // Há filtros em rascunho ainda não aplicados? (controla o destaque do botão Aplicar)
    hasPendingChanges () {
      const draft = JSON.stringify({
        users: this.filterUsers,
        date: this.filterDate,
        type: this.filterType,
        hangupCause: this.filterHangupCause
      })
      const applied = JSON.stringify({
        users: this.applied.users,
        date: this.applied.date,
        type: this.applied.type,
        hangupCause: this.applied.hangupCause
      })
      return draft !== applied
    }
  },
  created () {
    // Perfil (antes do primeiro render, para os filhos receberem props corretas).
    const user = (this.$store.state.auth && this.$store.state.auth.user) || {}
    this.isAdmin = user.role === 'ADMIN'
    this.ownExtension = (user.extension !== undefined && user.extension !== null) ? String(user.extension) : null
    if (!this.isAdmin && this.ownExtension) {
      // Não-admin: filtro fica oculto; só os dados vêm escopados ao próprio ramal.
      this.filterUsers = [this.ownExtension]
    }
    // Admin: filterUsers vazio = TODOS (default, exibido como todos marcados).
    // Sincroniza o snapshot aplicado com o rascunho inicial.
    this.snapshotApplied()
    // Defaults de fonte/tooltip dos charts (antes dos filhos criarem as instâncias).
    applyChartDefaults(Chart)
  },
  mounted () {
    this.loadMoreUsers()
    this.loadData()
    this.countdown()
  },
  methods: {
    // Monta o filtro enviado ao data-source (período + tipo + usuários + status [+ restrição de perfil]).
    buildFilter () {
      const filter = Object.assign(
        JSON.parse(this.applied.type),
        JSON.parse(this.applied.date),
        {
          filterUsers: this.applied.users,
          filterHangupCause: this.applied.hangupCause
        }
      )
      if (!this.isAdmin && this.ownExtension) {
        filter.restrictExtension = this.ownExtension
      }
      return filter
    },
    // Copia o rascunho (filter*) para os filtros aplicados.
    snapshotApplied () {
      this.applied = {
        users: this.filterUsers.slice(),
        date: this.filterDate,
        type: this.filterType,
        hangupCause: this.filterHangupCause.slice()
      }
    },
    // Carrega a próxima página de ramais (infinite scroll, 100 por vez) — só admin.
    // Devolve a promise da página; se já há uma em voo, devolve a dela (não dispara outra).
    loadMoreUsers () {
      if (this.userOptionsLoading) return this.pendingUserOptions
      if (!this.isAdmin || !this.userOptionsHasMore) return Promise.resolve()
      const request = ++this.userOptionsRequest
      this.userOptionsLoading = true
      this.userOptionsError = null
      this.pendingUserOptions = CDRDataSource.userOptions(this.userOptionsSkip, USER_OPTIONS_PAGE_SIZE, this.userSearch)
        .then(({ options, hasMore }) => {
          if (request !== this.userOptionsRequest) return
          const seen = new Set(this.userOptions.map(o => o.value))
          const fresh = options.filter(o => !seen.has(o.value))
          this.userOptions = this.userOptions.concat(fresh)
          const labels = Object.assign({}, this.userLabels)
          options.forEach(o => { labels[o.value] = o.text })
          this.userLabels = labels
          this.userOptionsSkip += USER_OPTIONS_PAGE_SIZE
          this.userOptionsHasMore = hasMore
        })
        .catch(err => {
          if (request !== this.userOptionsRequest) return
          // hasMore continua true: o "Tentar novamente" repete a mesma página.
          this.userOptionsError = err
          console.warn('Dashboard userOptions error:', err)
        })
        .then(() => {
          if (request === this.userOptionsRequest) this.userOptionsLoading = false
        })
      return this.pendingUserOptions
    },
    // "Selecionar todos": carrega as páginas que faltam (do termo atual) e marca todos os
    // exibidos, preservando o resto da seleção. Sem isso, marcaria só os já carregados e o
    // Dashboard ficaria restrito a eles sem aviso. Aborta se a busca mudar ou der erro
    // (o erro aparece no filtro, com "Tentar novamente").
    // "Selecionar todos"/"Selecionar resultados".
    // Sem busca: seleção vazia, que o buildFilter já trata como TODOS — evita carregar a
    // organização inteira (23 páginas na maior conta) e mandar milhares de ramais na URL.
    // Com busca: carrega as páginas restantes do termo e marca os resultados, até o teto.
    selectAllUsers () {
      if (!this.isAdmin) return Promise.resolve()
      this.userOptionsTooMany = false
      if (!this.userSearch) {
        this.filterUsers = []
        return Promise.resolve()
      }
      const search = this.userSearch
      const loadRemaining = () => {
        if (this.userSearch !== search || this.userOptionsError) return Promise.resolve(false)
        if (!this.userOptionsHasMore) return Promise.resolve(true)
        return this.loadMoreUsers().then(loadRemaining)
      }
      return loadRemaining().then(done => {
        if (!done) return
        const shown = this.filterUsersOptions.map(o => o.value)
        if (shown.length > MAX_SELECTABLE_USERS) {
          this.userOptionsTooMany = true
          return
        }
        const missing = shown.filter(value => this.filterUsers.indexOf(value) < 0)
        this.filterUsers = this.filterUsers.concat(missing)
      })
    },
    // Nova busca por nome (já com debounce no filtro): recomeça a paginação do zero.
    searchUsers (term) {
      const name = (term || '').trim()
      if (name === this.userSearch) return
      this.userSearch = name
      this.userOptionsRequest++ // invalida a resposta da busca anterior, se ainda em voo
      this.userOptions = []
      this.userOptionsSkip = 0
      this.userOptionsHasMore = true
      this.userOptionsLoading = false
      this.userOptionsError = null
      this.userOptionsTooMany = false
      this.loadMoreUsers()
    },
    // Busca os CDRs e recalcula todas as agregações de uma vez.
    loadData () {
      return CDRDataSource.refreshDataFromAPI(this.buildFilter())
        .then(() => {
          this.error = null
          return Promise.all([
            CDRDataSource.callStatistics(),
            CDRDataSource.hangupcauseStatistics(),
            CDRDataSource.extensionStatistics(this.granularity),
            CDRDataSource.patienceStatistics()
          ])
        })
        .then(([kpi, donut, ext, patience]) => {
          this.calculateStatistics(kpi.calls, kpi.answers, kpi.durations)
          this.donutData = donut
          this.chartData = ext
          this.extensionsTotal = ext.extensions ? Object.keys(ext.extensions).length : 0
          this.patienceByExtension = patience.byExtension
        })
        .catch(err => {
          this.error = err
          console.warn('Dashboard loadData error:', err)
        })
    },
    refreshData () {
      this.refreshCount = this.refreshTime
      return this.loadData()
    },
    countdown () {
      this.timer = _ => {
        if (this.refreshCount === 0) {
          this.refreshData().then(_ => {
            if (typeof this.timer === 'function') this.timer()
          })
        } else {
          this.timeout = setTimeout(_ => {
            this.refreshCount--
            if (typeof this.timer === 'function') this.timer()
          }, 1000)
        }
      }
      this.timer()
    },
    // Aplica o rascunho de filtros: snapshot + reinicia o contador e dispara UM reload.
    applyFilters () {
      this.snapshotApplied()
      const period = JSON.parse(this.applied.date)
      this.refreshTime = period.refreshTime
      this.timer = null
      clearTimeout(this.timeout)
      this.countdown()
      this.$nextTick(() => this.refreshData())
    },
    // Compara duas listas como conjunto (ignora ordem) — usado p/ ignorar emissões redundantes.
    sameSet (a, b) {
      return a.length === b.length && a.every(v => b.indexOf(v) >= 0)
    },
    // Os handlers abaixo só editam o RASCUNHO — nada recarrega até clicar em Aplicar.
    // O guard ignora reemissões de mesmo conteúdo (eco do checkbox-group) — evita loop reativo.
    onChangeUsers (value) {
      if (this.sameSet(value, this.filterUsers)) return
      this.filterUsers = value
    },
    onChangeStatus (value) {
      if (this.sameSet(value, this.filterHangupCause)) return
      this.filterHangupCause = value
    },
    onChangeDate (value) {
      this.filterDate = value
    },
    onChangeType (value) {
      this.filterType = value
    },
    // Cross-filter pelo gráfico (donut/legenda/barra da tabela): aplica NA HORA — diferente do
    // multi-select do dropdown (que usa o botão Aplicar). Sem isso, o clique só fica em rascunho.
    toggleStatusFilter (label) {
      if (!label) return
      const idx = this.filterHangupCause.indexOf(label)
      if (idx >= 0) {
        this.filterHangupCause.splice(idx, 1)
      } else {
        this.filterHangupCause.push(label)
      }
      this.applyFilters()
    },
    // Drill de período: só o gráfico "Chamadas por usuário" muda (refetch leve).
    setGranularity (level) {
      if (!level || level === this.granularity) return
      this.granularity = level
      CDRDataSource.extensionStatistics(this.granularity)
        .then(ext => {
          this.chartData = ext
        })
        .catch(err => {
          console.warn('CDRDataSource.extensionStatistics (drill):', err)
        })
    },
    buildReportFilters () {
      const typeOption = this.filterTypeOptions.find(option => option.value === this.applied.type)
      return [
        { label: 'Período', value: formatPeriodLabel(this.applied.date, this.filterDateOptions) },
        { label: 'Tipo de chamada', value: typeOption.text },
        { label: 'Usuários', value: this.appliedUsersLabel() },
        { label: 'Status da chamada', value: this.appliedStatusLabel() }
      ]
    },
    appliedUsersLabel () {
      if (this.applied.users.length === 0) return 'Todos'
      return this.applied.users
        // userLabels, não a lista exibida: com busca ativa ela só tem quem casa com o termo.
        .map(extension => this.userLabels[extension] || extension)
        .join(', ')
    },
    appliedStatusLabel () {
      if (this.applied.hangupCause.length === 0) return 'Todos'
      return this.applied.hangupCause.join(', ')
    },
    buildReportKpis () {
      return [
        { label: 'Total de chamadas', value: String(this.callsTotal) },
        { label: 'Taxa de atendimento', value: `${this.hitRate}%` },
        { label: 'Chamadas atendidas', value: String(this.answersTotal) },
        { label: 'Tempo falado', value: this.durationsTotalTime },
        { label: 'Total de ramais', value: String(this.extensionsTotal) },
        { label: 'Tempo médio de atendimento', value: this.tma }
      ]
    },
    buildReportStatuses () {
      return this.donutData.labels.map(label => ({
        label,
        count: String(this.donutData.hangups[label]),
        color: statusColor(label)
      }))
    },
    exportReportHtml ({ columns, rows }) {
      const generationDate = new Date()
      const html = generateDashboardReportHtml({
        organizationName: this.$store.state.auth.organization.name,
        generatedAt: formatReportTimestamp(generationDate),
        filters: this.buildReportFilters(),
        kpis: this.buildReportKpis(),
        statuses: this.buildReportStatuses(),
        columns,
        rows
      })
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }))
      const link = document.createElement('a')
      link.href = url
      link.download = `desempenho-por-usuario-${formatIsoDay(generationDate)}.html`
      link.click()
      URL.revokeObjectURL(url)
    },
    calculateStatistics (calls, answers, durations) {
      this.callsTotal = Object.entries(calls).reduce((total, obj) => {
        total += obj[1]
        return total
      }, 0)
      this.answersTotal = Object.entries(answers).reduce((total, obj) => {
        total += obj[1]
        return total
      }, 0)
      this.durationsTotal = Object.entries(durations).reduce((total, obj) => {
        total += obj[1]
        return total
      }, 0)
      this.durationsTotalTime = secondsToTimeParser(this.durationsTotal)
      this.tma = secondsToTimeParser(this.durationsTotal / (this.answersTotal === 0 ? 1 : this.answersTotal))
      this.hitRate = Math.round(((this.answersTotal / (this.callsTotal === 0 ? 1 : this.callsTotal)) * 100) * 100) / 100
    }
  },
  beforeDestroy () {
    clearTimeout(this.timeout)
    this.timer = null
  }
}
</script>

<style lang="scss" scoped></style>

<style src="./Dashboard/dashboard.css"></style>



// WEBPACK FOOTER //
// src/components/Dashboard.vue